import { clone } from 'lodash-es'
import type { ITxDataTableColumn, ITxDataTableGenerateDataColumns, ITxDataTableVisibleColumn } from '@/shared/components/txDataTable/TxDataTable.types'
import { AttributeType } from '@/models/catalogAttribute'
import { changeLogConstants } from '@/models/constants'
import type { IChangeLogField, IChangeLogFields } from '@/api/t1/model/changeLog'

export default class ChangeLogModel {
  modelPath: string
  generateDataFields: ITxDataTableGenerateDataColumns[]
  fields: IChangeLogField[]
  constructor(t, te, fieldsDropDownData?: IChangeLogFields, allowedCalcFields?: string[], defaultChangeLogReportColumns?: string[]) {
    this.modelPath = 'changeLog'
    this.fields = []
    this.generateDataFields = this.getGenerateDataFields(t, te, fieldsDropDownData, allowedCalcFields, defaultChangeLogReportColumns)
  }

  private getGenerateDataFields(t, te, fieldsDropDownData?: IChangeLogFields, allowedCalcFields?: string[], defaultChangeLogReportColumns?: string[]) {
    let fields = fieldsDropDownData?.Fields || []
    fields = fields.filter(field => field.TypeId !== AttributeType.Calc || (allowedCalcFields && allowedCalcFields.includes(field.SystemName)))
    const defaultValuesForColumnsToInclude: string[] = []
    if (defaultChangeLogReportColumns && defaultChangeLogReportColumns.length) {
      let configuredColumns = fields.filter(field => defaultChangeLogReportColumns.includes(field.SystemName))
      configuredColumns = configuredColumns.length > 10 ? configuredColumns.slice(0, 9) : configuredColumns
      configuredColumns?.forEach((configuredColumn) => {
        defaultValuesForColumnsToInclude.push(`${configuredColumn.SystemName}|${configuredColumn.FieldType}`)
      })
    }
    this.fields = clone(fields)
    fields = fields.map(item => ({ ...item, SystemName: `${item.SystemName}|${item.FieldType}`, Name: (te(`changeLog.staticFields.${item.SystemName}`) ? t(`changeLog.staticFields.${item.SystemName}`) : item.Name) + (item.FieldType === changeLogConstants.fieldTypes.priceGroup || item.FieldType === changeLogConstants.fieldTypes.segmentation ? ` (${item.FieldType === changeLogConstants.fieldTypes.priceGroup ? t('changeLog.fieldType.priceGroup') : t('changeLog.fieldType.segmentation')})` : '') }))
    const today = new Date()
    const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())
    return [
      { key: 'Seasons', label: t('changeLog.generateDataFields.seasons'), data: fieldsDropDownData ? fieldsDropDownData.Seasons : [], type: 'list', multiple: true, clearable: true, filterable: true, defaultValue: [] },
      { key: 'Accounts', label: t('changeLog.generateDataFields.accounts'), data: fieldsDropDownData ? fieldsDropDownData.Accounts : [], type: 'list', multiple: true, clearable: true, filterable: true, defaultValue: [] },
      { key: 'Fields', label: t('changeLog.generateDataFields.fields'), data: fields, type: 'list', displayProp: 'Name', valueProp: 'SystemName', multiple: true, multipleLimit: 10, clearable: true, filterable: true, required: true, defaultValue: '' },
      { key: 'DateRange', label: t('changeLog.generateDataFields.dateRange'), type: 'dateRange', clearable: true, defaultValue: [oneMonthAgo.toISOString(), today.toISOString()], required: true },
      { key: 'ChangedBy', label: t('changeLog.generateDataFields.changedBy'), data: fieldsDropDownData ? fieldsDropDownData.Users : [], type: 'list', multiple: true, clearable: true, filterable: true, defaultValue: [] },
      { key: 'OutputFields', label: t('changeLog.generateDataFields.columnsToIncludeInReport'), data: fields, displayProp: 'Name', valueProp: 'SystemName', type: 'list', multiple: true, multipleLimit: 10, clearable: true, filterable: true, defaultValue: defaultValuesForColumnsToInclude },
      { key: 'Articles', label: t('changeLog.generateDataFields.articles'), type: 'textArea', defaultValue: '' },
      { key: 'Models', label: t('changeLog.generateDataFields.models'), type: 'textArea', defaultValue: '' },
    ] as ITxDataTableGenerateDataColumns[]
  }

  private getStaticColumns(t) {
    const fieldsMap = new Map()
    const fieldsTypeMap = new Map()
    this.fields.forEach((field) => {
      fieldsMap.set(field.SystemName, field.Name)
      fieldsTypeMap.set(field.SystemName, field.TypeId === 2 ? AttributeType.Status : field.TypeId)
    })
    const fields: ITxDataTableColumn[] = [
      { property: 'Catalog', title: t('changeLog.columns.catalog'), type: AttributeType.Nvarchar, allowFilterAndSort: false } as ITxDataTableColumn,
      { property: 'ArticleNumber', title: t('changeLog.columns.articleNumber'), type: AttributeType.Nvarchar, allowFilterAndSort: false } as ITxDataTableColumn,
      { property: 'ModelNumber', title: t('changeLog.columns.modelNumber'), type: AttributeType.Nvarchar, allowFilterAndSort: false } as ITxDataTableColumn,
      { property: 'ModelName', title: t('changeLog.columns.modelName'), type: AttributeType.Nvarchar, allowFilterAndSort: false } as ITxDataTableColumn,
      { property: 'ImpactedField', title: t('changeLog.columns.impactedAttribute'), type: AttributeType.Nvarchar, filterLookup: fieldsMap, allowFilterAndSort: false } as ITxDataTableColumn,
      { property: 'PreviousValue', title: t('changeLog.columns.previousValue'), type: AttributeType.Nvarchar, typeLookup: fieldsTypeMap, typeLookupField: 'ImpactedField', allowFilterAndSort: false } as ITxDataTableColumn,
      { property: 'NewValue', title: t('changeLog.columns.newValue'), type: AttributeType.Nvarchar, typeLookup: fieldsTypeMap, typeLookupField: 'ImpactedField', allowFilterAndSort: false } as ITxDataTableColumn,
      { property: 'ChangeDate', title: t('changeLog.columns.changedDate'), type: AttributeType.DateWithTimeTooltip, allowFilterAndSort: false } as ITxDataTableColumn,
      { property: 'ChangeBy', title: t('changeLog.columns.changedBy'), type: AttributeType.Nvarchar, allowFilterAndSort: false } as ITxDataTableColumn,
    ]
    return fields
  }

  getDataTableColumns(t, selectedFieldsInReport: string[] = []) {
    const staticColumns: ITxDataTableColumn[] = this.getStaticColumns(t)
    const columnsToIncludeField = this.generateDataFields.find(generatedField => generatedField.key === 'OutputFields')
    const allFields: IChangeLogField[] = []
    if (columnsToIncludeField && columnsToIncludeField.data) {
      selectedFieldsInReport.forEach((field) => {
        const object = columnsToIncludeField.data?.find(data => data.SystemName === field)
        if (object) {
          allFields.push(object)
        }
      })
    }
    if (allFields.length) {
      const dynamicFields: ITxDataTableColumn[] = []
      allFields.forEach((o) => {
        const systemName = o.SystemName.split('|')
        dynamicFields.push({
          property: systemName[0],
          title: o.Name,
          type: o.TypeId === 2 ? AttributeType.Status : o.TypeId,
          allowFilterAndSort: false,
        })
      })
      // .map(o => ({
      //   const systemName = o.SystemName.split('|')
      //   property: systemName,
      //   title: o.Name,
      //   type: o.TypeId,
      //   allowFilterAndSort: false,
      // }))
      return staticColumns.concat(dynamicFields)
    }
    else {
      return staticColumns
    }
  }

  getDefaultVisibleColumnList(columns) {
    const defaultFieldObjects: ITxDataTableVisibleColumn[] = columns
      .map(o => ({
        property: o.property,
        order: 1,
        width: 0.175,
      }))

    return defaultFieldObjects
  }
}
